package com.sddamico.draftmachine.ds.motion

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.MutationObserver
import org.w3c.dom.MutationObserverInit
import kotlin.js.Promise

/**
 * What a change of screen or state means to the person watching. The kind picks the choreography in
 * [installMotionStyles]: hierarchy moves sideways, task sheets move vertically, and in-place changes
 * keep the page still while the pieces that changed travel.
 */
enum class Motion(internal val attr: String) {
  /** Deeper into the app: the new screen arrives from the right. */
  Forward("forward"),
  /** Back up the hierarchy: the new screen arrives from the left. */
  Back("back"),
  /** A task sheet rises over the current screen, which recedes beneath it. */
  Present("present"),
  /** The task sheet drops away and the screen beneath comes forward. */
  Dismiss("dismiss"),
  /** Same level: sibling tabs, a redirect, a load finishing. */
  Fade("fade"),
  /** The screen stays; rows and panels glide from their old place to their new one. */
  Update("update"),
}

/**
 * Runs state changes inside the browser's View Transitions API. The browser snapshots the screen,
 * the change is applied, and the old and new snapshots are animated by the `::view-transition-*`
 * rules in [installMotionStyles]. Browsers without the API, and people who ask for reduced motion,
 * get the change applied immediately.
 *
 * Compose for Web renders on `requestAnimationFrame`, and the browser suspends animation frames
 * while it waits for a transition's update callback, so a plain update would sit unrendered until
 * the browser gives up. While the callback runs, frames are routed through a macrotask so Compose
 * can render the new screen in time to be captured.
 */
object ViewTransitions {
  // Long enough that a typical page's data lands inside the transition, so it animates straight to
  // content; short enough that a slow request falls back to its loading state without feeling stuck.
  private const val HOLD_FOR_DATA_MS = 300.0
  // Compose applies a state change over a couple of macrotasks; the DOM has settled once it has been
  // quiet this long.
  private const val QUIET_MS = 24.0
  // The screen is frozen while it waits, so never longer than this, whatever the page is doing.
  private const val SETTLE_LIMIT_MS = 600.0

  private const val KIND_ATTR = "data-dm-vt"
  private const val PHASE_ATTR = "data-dm-vt-phase"
  private const val PAGE_MORPH_ATTR = "data-dm-vt-page-morph"
  private const val MORPHED_ATTR = "data-dm-vt-morphed"
  internal const val MORPH_KEY_ATTR = "data-dm-morph"
  internal const val CUE_NAME_ATTR = "data-dm-cue"
  internal const val CUE_CLASS_ATTR = "data-dm-cue-class"
  internal const val PENDING_ATTR = "data-dm-pending"
  internal const val MORPH_NAME = "dm-morph"

  private var running: dynamic = null
  // A transition skipped before its callback ran still gets its callback, so two captures can overlap.
  private var captures = 0
  private var nativeFrame: dynamic = null

  /** True while a transition is waiting for the new screen to render, before it starts animating. */
  val isCapturing: Boolean get() = captures > 0

  private val supported: Boolean
    get() = document.asDynamic().startViewTransition != undefined

  private val reducedMotion: Boolean
    get() = window.matchMedia("(prefers-reduced-motion: reduce)").matches

  /**
   * Apply [update] as a [motion] transition. A null [motion] applies it with no animation.
   *
   * [morphKey] names an element (tagged with [Modifier.morphKey][morphKey]) that should grow into
   * the incoming screen, or that the outgoing screen should shrink back into. Whichever snapshot
   * holds a visible element with that key uses it in place of the whole page.
   *
   * Calls made while another transition is capturing its new state join that transition instead of
   * starting one, so a redirect fired by a page as it mounts lands in the same animation.
   */
  fun run(motion: Motion?, morphKey: String? = null, update: () -> Unit) {
    if (captures > 0) {
      update()
      return
    }
    if (motion == null || !supported || reducedMotion || document.asDynamic().hidden == true) {
      update()
      return
    }
    running?.skipTransition()

    val root = document.documentElement!!
    root.setAttribute(KIND_ATTR, motion.attr)
    root.removeAttribute(PHASE_ATTR)
    clearMorph()
    // When the outgoing screen has no element for the key, the whole page stands in for it, in case
    // the incoming screen has one to shrink into.
    val oldHasKey = tagMorph(morphKey)
    if (morphKey != null && !oldHasKey) root.setAttribute(PAGE_MORPH_ATTR, "")
    val cues = motion == Motion.Update
    clearCues()
    if (cues) tagCues()

    val callback: () -> Promise<Unit> = {
      captures++
      routeFramesThroughTasks()
      try {
        update()
      } catch (e: Throwable) {
        endCapture()
        throw e
      }
      settle().then {
        root.setAttribute(PHASE_ATTR, "new")
        clearMorph()
        clearCues()
        if (cues) tagCues()
        val newHasKey = tagMorph(morphKey)
        if (oldHasKey && !newHasKey) root.setAttribute(PAGE_MORPH_ATTR, "")
        if (oldHasKey != newHasKey) root.setAttribute(MORPHED_ATTR, "")
        endCapture()
      }
    }
    val transition: dynamic = document.asDynamic().startViewTransition(callback)
    running = transition
    // A skipped or aborted transition rejects `ready`; the update itself still applied.
    transition.ready.catch { _: dynamic -> }
    val finish: (dynamic) -> Unit = {
      if (running === transition) {
        running = null
        root.removeAttribute(KIND_ATTR)
        root.removeAttribute(PHASE_ATTR)
        root.removeAttribute(MORPHED_ATTR)
        clearMorph()
        clearCues()
      }
    }
    transition.finished.then(finish, finish)
  }

  private fun endCapture() {
    captures--
    if (captures == 0) restoreFrames()
  }

  private fun routeFramesThroughTasks() {
    if (nativeFrame != null) return
    val w = window.asDynamic()
    nativeFrame = w.requestAnimationFrame
    w.requestAnimationFrame = { callback: dynamic ->
      window.setTimeout({ callback(window.performance.now()) }, 0)
    }
  }

  private fun restoreFrames() {
    if (nativeFrame == null) return
    window.asDynamic().requestAnimationFrame = nativeFrame
    nativeFrame = null
  }

  /**
   * Resolves once the new screen has rendered: the DOM has stopped changing and no loading
   * placeholder ([pendingContent]) is on screen, or the data hold has run out.
   */
  private fun settle(): Promise<Unit> = Promise { resolve, _ ->
    val start = window.performance.now()
    var lastChange = start
    val observer = MutationObserver { _, _ -> lastChange = window.performance.now() }
    observer.observe(
      document.body!!,
      MutationObserverInit(childList = true, subtree = true, attributes = true, characterData = true),
    )
    fun check() {
      val now = window.performance.now()
      val quiet = now - lastChange >= QUIET_MS
      val loading = document.querySelector("[$PENDING_ATTR]") != null
      val settled = quiet && (!loading || now - start >= HOLD_FOR_DATA_MS)
      if (settled || now - start >= SETTLE_LIMIT_MS) {
        observer.disconnect()
        resolve(Unit)
      } else {
        window.setTimeout({ check() }, 8)
      }
    }
    window.setTimeout({ check() }, 8)
  }

  private fun tagMorph(key: String?): Boolean {
    if (key == null) return false
    val el = document.querySelector("[$MORPH_KEY_ATTR=\"$key\"]") as? HTMLElement ?: return false
    val rect = el.getBoundingClientRect()
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false
    el.style.setProperty("view-transition-name", MORPH_NAME)
    return true
  }

  // Cue names carry the value they show, so they are set inline, and only around the captures.
  private fun tagCues() {
    forEachElement("[$CUE_NAME_ATTR]") { el ->
      el.style.setProperty("view-transition-name", el.getAttribute(CUE_NAME_ATTR)!!)
      el.style.setProperty("view-transition-class", el.getAttribute(CUE_CLASS_ATTR)!!)
    }
  }

  private fun clearCues() {
    forEachElement("[$CUE_NAME_ATTR]") { el ->
      el.style.removeProperty("view-transition-name")
      el.style.removeProperty("view-transition-class")
    }
  }

  private fun forEachElement(selector: String, action: (HTMLElement) -> Unit) {
    val found = document.querySelectorAll(selector)
    for (i in 0 until found.length) (found.item(i) as? HTMLElement)?.let(action)
  }

  private fun clearMorph() {
    forEachElement("[$MORPH_KEY_ATTR]") { it.style.removeProperty("view-transition-name") }
    document.documentElement!!.removeAttribute(PAGE_MORPH_ATTR)
  }
}
